// src/admin/pages/AdminEditProduct.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaArrowLeft, FaImage, FaPlus, FaTrash } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminProductById,
  updateAdminProduct,
  getAdminCategories,
  getAdminTags,
} from "../../services/adminProducts.api";
import { uploadAdminFile } from "../../services/adminUpload.api";
import { deleteAdminProductImage } from "../../services/adminProducts.api";
import { deleteUploadedFile } from "../../services/adminUpload.api";

function AdminEditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // EXTRA PRODUCT STATES (for edit)
  const [publicId, setPublicId] = useState("");

  const [isBestseller, setIsBestseller] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [onSale, setOnSale] = useState(false);
  const [salePercent, setSalePercent] = useState(0);
  
  const [materialEn, setMaterialEn] = useState("");
  const [materialAr, setMaterialAr] = useState("");
  const [heelHeight, setHeelHeight] = useState("");
  
  const [selectedFileNames, setSelectedFileNames] = useState({});

  // relations
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // variants (flat list)
  const [variants, setVariants] = useState([]);

  // lists
  const [allCategories, setAllCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // PRODUCT STATES
  const [titleEn, setTitleEn] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // old states in your file (kept to avoid “changing everything else”)
  const [stock, setStock] = useState("");
  const [category, setCategory] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [realProductId, setRealProductId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [descEn, setDescEn] = useState("");
  const [descAr, setDescAr] = useState("");
  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const [customHex, setCustomHex] = useState("");
  const [customColorName, setCustomColorName] = useState("");

  const [images, setImages] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  
  // GLOBAL alt for all images
 const [globalAltEn, setGlobalAltEn] = useState("");
 const [globalAltAr, setGlobalAltAr] = useState("");


  const isValidHex = (hex) =>
  /^#([0-9A-Fa-f]{6})$/.test(hex);

  const WOMEN_COLOR_PALETTE = [
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#ffffff" },
  { name: "Beige", hex: "#f5f5dc" },
  { name: "Brown", hex: "#8b4513" },
  { name: "Tan", hex: "#d2b48c" },
  { name: "Nude", hex: "#e3c6a8" },
  { name: "Red", hex: "#c1121f" },
  { name: "Burgundy", hex: "#800020" },
  { name: "Pink", hex: "#f4a6b8" },
  { name: "Blush", hex: "#f2c1cc" },
  { name: "Purple", hex: "#6a0dad" },
  { name: "Blue", hex: "#1e3a8a" },
  { name: "Navy", hex: "#0a2540" },
  { name: "Green", hex: "#2f6f4e" },
  { name: "Olive", hex: "#6b8e23" },
  { name: "Gold", hex: "#c9a227" },
  { name: "Silver", hex: "#c0c0c0" },
];

const handleMultiImageUpload = async (files) => {
  if (!files || !files.length) return;

  const startOrder = images.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      const uploaded = await uploadAdminFile(file);

      setImages((prev) => [
        ...prev,
        {
          id: null, // new image
          url: uploaded.url,
          alt_en: globalAltEn,
          alt_ar: globalAltAr,
          color_name: "",
          sort_order: startOrder + i + 1,
          media_type: file.type?.startsWith("video")
            ? "video"
            : "image",
        },
      ]);
    } catch (err) {
      alert(err.message || "Upload failed");
    }
  }
};


useEffect(() => {
  setImages((prev) =>
    (prev || []).map((img) => ({
      ...img,
      alt_en: globalAltEn,
      alt_ar: globalAltAr,
    }))
  );
}, [globalAltEn, globalAltAr]);

  const TEXT = {
    EN: {
      loading: "Loading product...",
      back: "Back to Products",
      title: publicId
  ? `Edit Product (ID: ${publicId})`
  : "Edit Product",
      error: "Please fill all required fields (marked with *).",
      basic: "Basic Info",
      desc: "Descriptions",
      pricing: "Pricing & Flags",
      image: "Product Image",
      titleEn: "Name (EN) *",
      titleAr: "Name (AR)",
      materialEn: "Material (EN) *",
      materialAr: "Material (AR) *",
      heelHeight: "Heel Height (cm) *",
      descEn: "Description (EN)",
      descAr: "Description (AR)",
      price: "Price ($) *",
      publicId: "Public ID *",
      flags: "Flags",
      bestseller: "Bestseller",
      new: "New",
      sale: "On Sale",
      salePercent: "Sale %",
      categories: "Categories (multi-select)",
      tags: "Tags (multi-select)",
      variants: "Variants (Colors / Sizes / Qty)",
      addColor: "Add Color",
      addSize: "Add Size",
      colorName: "Color name",
      colorHex: "Color hex",
      size: "Size",
      qty: "Qty",
      remove: "Remove",
      cancel: "Cancel",
      saving: "Saving...",
      update: "Update Product",
      selectAtLeastOneCategory: "Select at least 1 category.",
      sortOrder: "Sort order",
    },
    AR: {
      loading: "جاري تحميل المنتج...",
      back: "العودة إلى المنتجات",
      title: publicId
  ? `تعديل المنتج (رقم: ${publicId})`
  : "تعديل المنتج",
      error: "يرجى تعبئة جميع الحقول المطلوبة (*).",
      basic: "المعلومات الأساسية",
      desc: "الوصف",
      pricing: "السعر والخصائص",
      image: "صورة المنتج",
      titleEn: "الاسم (إنجليزي) *",
      titleAr: "الاسم (عربي)",
      materialEn: "الخامة (إنجليزي) *",
      materialAr: "الخامة (عربي) *",
      heelHeight: "ارتفاع الكعب (سم) *",
      descEn: "الوصف (إنجليزي)",
      descAr: "الوصف (عربي)",
      price: "السعر ($) *",
      publicId: "المعرّف العام *",
      flags: "الخصائص",
      bestseller: "الأكثر مبيعاً",
      new: "جديد",
      sale: "تخفيض",
      salePercent: "نسبة التخفيض %",

      categories: "التصنيفات (متعدد)",
      tags: "الوسوم (متعدد)",
      variants: "المتغيرات (ألوان / مقاسات / كمية)",
      addColor: "إضافة لون",
      addSize: "إضافة مقاس",
      colorName: "اسم اللون",
      colorHex: "كود اللون",
      size: "المقاس",
      qty: "الكمية",
      remove: "حذف",

      cancel: "إلغاء",
      saving: "جاري الحفظ...",
      update: "تحديث المنتج",
      selectAtLeastOneCategory: "اختر تصنيف واحد على الأقل.",
      sortOrder: "ترتيب العرض",
    },
  }[lang];

  // Load product + categories + tags
  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([getAdminProductById(id), getAdminCategories(), getAdminTags()])
      .then(([p, cats, tags]) => {
        if (!alive) return;

        // product
        setTitleEn(p.name_en || "");
        setTitleAr(p.name_ar || "");
        setDescEn(p.description_en || "");
        setDescAr(p.description_ar || "");
        setMaterialEn(p.material_en || "");
        setMaterialAr(p.material_ar || "");
        setHeelHeight(p.heel_height ?? "");
        setPrice(p.base_price ?? "");
        setPublicId(p.public_id || "");
        setRealProductId(p.id);
        setImages(Array.isArray(p.images) ? p.images : []);

        setIsBestseller(!!p.is_bestseller);
        setIsNew(!!p.is_new_in);
        setOnSale(!!p.on_sale);
        setSalePercent(Number(p.sale_percent || 0));

        setSelectedCategories(Array.isArray(p.category_ids) ? p.category_ids : []);
        setSelectedTags(Array.isArray(p.tag_ids) ? p.tag_ids : []);
        setVariants(Array.isArray(p.variants) ? p.variants : []);
        
        // lists
        setAllCategories(Array.isArray(cats) ? cats : []);
        setAllTags(Array.isArray(tags) ? tags : []);

        // optional image if your backend returns it (safe)
        setImageUrl(p.image || "");
      })
      .catch((e) => {
        console.error(e);
        setError(e.message || "Error loading product.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  // Sale UX: if toggle off => percent becomes 0
  useEffect(() => {
    if (!onSale) setSalePercent(0);
  }, [onSale]);

  // -------- helpers: categories/tags ----------
  const toggleIdInArray = (arr, idNum) => {
    const has = arr.includes(idNum);
    if (has) return arr.filter((x) => x !== idNum);
    return [...arr, idNum];
  };

  // -------- variants editor helpers ----------
  const groupedVariants = useMemo(() => {
    // group by color_name + color_hex
    const map = new Map();
    (variants || []).forEach((v) => {
      const colorName = v.color_name || "Unknown";
      const colorHex = v.color_hex || "#cccccc";
      const key = `${colorName}|||${colorHex}`;

      if (!map.has(key)) {
        map.set(key, {
          color_name: colorName,
          color_hex: colorHex,
          rows: [],
        });
      }
map.get(key).rows.push({
  size: v.size != null ? String(v.size) : "",
  is_available: v.is_available ?? 1,
});

    });

    // sort sizes inside each color
    return Array.from(map.values()).map((g) => ({
  ...g,
  rows: g.rows, // ✅ keep insertion order
}));

  }, [variants]);

  const rebuildFlatVariantsFromGroups = (groups) => {
  const flat = [];

  groups.forEach((g) => {
    // 👇 if no sizes yet, keep a placeholder row
    if (!g.rows.length) {
      flat.push({
        color_name: g.color_name,
        color_hex: g.color_hex,
        size: "",
        stock_qty: 0,
      });
      return;
    }

    g.rows.forEach((r) => {
      const size = String(r.size || "").trim();
flat.push({
  color_name: g.color_name,
  color_hex: g.color_hex,
  size,
  stock_qty: 5, // 🔒 fixed
  is_available: r.is_available ?? 1,
});
    });
  });

  setVariants(flat);
};


 const addColorGroup = () => {
  setVariants((prev) => [
    ...(prev || []),
    {
      color_name: "",
      color_hex: "",
      size: "",
      stock_qty: 0,
    },
  ]);
};

const addImageRow = () => {
  setImages((prev) => [
    ...(prev || []),
    {
      id: null,
      url: "",
      alt_en: "",
      alt_ar: "",
      color_name: "",
      sort_order: (prev?.length || 0) + 1,
    },
  ]);
};

const updateImageRow = (idx, patch) => {
  setImages((prev) =>
    prev.map((img, i) => (i === idx ? { ...img, ...patch } : img))
  );
};

const removeImageRow = async (idx) => {
  const img = images[idx];

  try {
    // ✅ Existing image → delete from DB + disk
    if (img.id) {
      await deleteAdminProductImage(img.id);
    }
    // ✅ Newly uploaded (not saved yet) → delete from disk only
    else if (img.url) {
      await deleteUploadedFile(img.url);
    }
  } catch (err) {
    console.error("Image delete failed:", err);
  }

  // ✅ Remove from UI state
  setImages((prev) => prev.filter((_, i) => i !== idx));
};



  const removeColorGroup = (idx) => {
    const groups = groupedVariants.filter((_, i) => i !== idx);
    rebuildFlatVariantsFromGroups(groups);
  };

  const updateColorGroup = (idx, patch) => {
    const groups = groupedVariants.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    rebuildFlatVariantsFromGroups(groups);
  };

  const addSizeRow = (idx) => {
    const groups = groupedVariants.map((g, i) =>
      i === idx ? { ...g, rows: [...g.rows, { size: "", is_available: 1 }] } : g
    );
    rebuildFlatVariantsFromGroups(groups);
  };

  const removeSizeRow = (idx, rowIdx) => {
    const groups = groupedVariants.map((g, i) =>
      i === idx ? { ...g, rows: g.rows.filter((_, j) => j !== rowIdx) } : g
    );
    rebuildFlatVariantsFromGroups(groups);
  };

  const updateSizeRow = (idx, rowIdx, patch) => {
    const groups = groupedVariants.map((g, i) => {
      if (i !== idx) return g;
      const newRows = g.rows.map((r, j) => (j === rowIdx ? { ...r, ...patch } : r));
      return { ...g, rows: newRows };
    });
    rebuildFlatVariantsFromGroups(groups);
  };

  // -------- save ----------
  const confirmSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!titleEn.trim() || !publicId.trim() || price === "" || Number(price) < 0) {
      setError(TEXT.error);
      return;
    }

    if (!selectedCategories.length) {
      setError(TEXT.selectAtLeastOneCategory);
      return;
    }

    if (
  !materialEn.trim() ||
  !materialAr.trim() ||
  heelHeight === "" ||
  Number(heelHeight) < 0
) {
  setError(TEXT.error);
  return;
}


    // Clean variants: remove empty sizes + normalize
    const cleanedVariants = (variants || [])
  .map((v) => {
    const color = (v.color_name || "Unknown").trim();
    const size = String(v.size || "").trim();

return {
  sku: `${publicId}-${color}-${size}`.replace(/\s+/g, "-").toUpperCase(),
  color_name: color,
  color_hex: (v.color_hex || "#cccccc").trim(),
  size,
  stock_qty: 5, // 🔒 fixed
  is_available: v.is_available ? 1 : 0,
};
  })
  .filter((v) => v.size);

const cleanedImages = images
  .filter((i) => i.url)
  .map((i, idx) => ({
    url: i.url,
    alt_en: i.alt_en || "",
    alt_ar: i.alt_ar || "",
    color_name: i.color_name || "",
    sort_order: Number(i.sort_order ?? idx + 1),
  }));


    setSaving(true);
    try {
      await updateAdminProduct(id, {
        name_en: titleEn.trim(),
        name_ar: titleAr.trim(),
        description_en: descEn.trim(),
        description_ar: descAr.trim(),
        material_en: materialEn.trim(),
        material_ar: materialAr.trim(),
        heel_height: Number(heelHeight),

        public_id: publicId.trim(),
        base_price: Number(price).toFixed(2),

        is_bestseller: isBestseller,
        is_new_in: isNew,
        on_sale: onSale,
        sale_percent: onSale ? Number(salePercent || 0) : 0,

        categories: selectedCategories,
        tags: selectedTags,
        variants: cleanedVariants,
        images: cleanedImages,
      });

      setShowToast(true);
      setTimeout(() => {
                 navigate("/admin/products");
                }, 1200);
    } catch (err) {
      console.error(err);
      setError(err.message || "Save error");
    } finally {
      setSaving(false);
    }
  };

  const discountedPrice = useMemo(() => {
  if (!onSale || !price || !salePercent) return "";
  const p = Number(price);
  const d = p - (p * Number(salePercent)) / 100;
  return d.toFixed(2);
}, [price, salePercent, onSale]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-600 text-xl">
        {TEXT.loading}
      </div>
    );
  }

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 max-w-5xl">
         {showConfirm && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        Confirm Update
      </h3>

      <p className="text-sm text-gray-600 mb-6">
        Are you sure you want to update this product?
      </p>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => setShowConfirm(false)}
          className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-sm"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={confirmSave}
          className="px-4 py-2 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 text-sm"
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}

        {showToast && (
  <div className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg text-sm font-semibold animate-fade-in">
    ✅ Product updated successfully
  </div>
)}

          {/* Top bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <button
              onClick={() => navigate("/admin/products")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300"
            >
              <FaArrowLeft /> {TEXT.back}
            </button>

            <h1 className="text-2xl font-bold text-cyan-700">{TEXT.title}</h1>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form
           onSubmit={(e) => {
                  e.preventDefault();
                  setShowConfirm(true);
                 }}
            className="bg-white border border-gray-200 shadow-xl rounded-2xl p-6 space-y-6"
          >
            {/* BASIC INFO */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">{TEXT.basic}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm font-semibold">{TEXT.titleEn}</label>
                  <input
                    type="text"
                    value={titleEn}
                    onChange={(e) => setTitleEn(e.target.value)}
                    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">{TEXT.titleAr}</label>
                  <input
                    type="text"
                    value={titleAr}
                    onChange={(e) => setTitleAr(e.target.value)}
                    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-semibold">{TEXT.publicId}</label>
                  <input
                    type="text"
                    value={publicId}
                    onChange={(e) => setPublicId(e.target.value)}
                    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
                  />
                </div>

                <div>
  <label className="block mb-1 text-sm font-semibold">
    {TEXT.materialEn}
  </label>
  <input
    value={materialEn}
    onChange={(e) => setMaterialEn(e.target.value)}
    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
  />
</div>

<div>
  <label className="block mb-1 text-sm font-semibold">
    {TEXT.materialAr}
  </label>
  <input
    value={materialAr}
    onChange={(e) => setMaterialAr(e.target.value)}
    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
  />
</div>

<div>
  <label className="block mb-1 text-sm font-semibold">
    {TEXT.heelHeight}
  </label>
  <input
    type="number"
    min="0"
    step="0.1"
    value={heelHeight}
    onChange={(e) => setHeelHeight(e.target.value)}
    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
  />
</div>

              </div>
            </section>

{/* DESCRIPTIONS */}
<section>
  <h2 className="text-lg font-semibold mb-3 text-gray-800">
    {TEXT.desc}
  </h2>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label className="block mb-1 text-sm font-semibold">
        {TEXT.descEn}
      </label>
      <textarea
        value={descEn}
        onChange={(e) => setDescEn(e.target.value)}
        rows={5}
        className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400 resize-none"
        placeholder="English description…"
      />
    </div>

    <div>
      <label className="block mb-1 text-sm font-semibold">
        {TEXT.descAr}
      </label>
      <textarea
        value={descAr}
        onChange={(e) => setDescAr(e.target.value)}
        rows={5}
        dir="rtl"
        className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400 resize-none"
        placeholder="الوصف بالعربية…"
      />
    </div>
  </div>
</section>

            {/* PRICING + FLAGS */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">{TEXT.pricing}</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
  <label className="block mb-1 text-sm font-semibold">{TEXT.price}</label>
  <input
    type="number"
    value={price}
    onChange={(e) => setPrice(e.target.value)}
    className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
  />

  {/* Discounted price (read-only) */}
  <div className="mt-2">
    <label className="block mb-1 text-xs font-semibold text-gray-500">
      Discounted Price
    </label>
    <input
      type="text"
      disabled
      value={discountedPrice}
      className={`w-full p-3 rounded-xl border text-sm font-semibold
        ${onSale ? "text-red-600 bg-red-50" : "text-gray-400 bg-gray-100"}
      `}
      placeholder="—"
    />
  </div>
</div>


                {/* Sale toggle UX */}
                <div className="md:col-span-2 border rounded-xl p-4 bg-gray-50">
                  <div className="font-semibold mb-2 text-sm text-gray-700">{TEXT.flags}</div>

                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isBestseller}
                        onChange={(e) => setIsBestseller(e.target.checked)}
                      />
                      {TEXT.bestseller}
                    </label>

                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isNew}
                        onChange={(e) => setIsNew(e.target.checked)}
                      />
                      {TEXT.new}
                    </label>

                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={onSale}
                        onChange={(e) => setOnSale(e.target.checked)}
                      />
                      {TEXT.sale}
                    </label>

                    <div className={`flex items-center gap-2 ${!onSale ? "opacity-50" : ""}`}>
                      <span className="text-sm">{TEXT.salePercent}</span>
                      <input
                        type="number"
                        min="0"
                        max="95"
                        disabled={!onSale}
                        value={salePercent}
                        onChange={(e) => setSalePercent(e.target.value)}
                        className="w-24 p-2 border rounded-lg focus:ring-2 focus:ring-cyan-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CATEGORIES MULTI-SELECT */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">{TEXT.categories}</h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {allCategories.map((c) => {
                  const cid = Number(c.id);
                  const checked = selectedCategories.includes(cid);

                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer text-sm
                        ${checked ? "bg-cyan-50 border-cyan-300" : "bg-white hover:bg-gray-50"}
                      `}
                    >
                     <input
  type="checkbox"
  checked={checked}
  onChange={() =>
    setSelectedCategories((prev) => toggleIdInArray(prev, cid))
  }
/>


                      <span className="truncate">{c.slug}</span>
                    </label>
                  );
                })}
              </div>

              {selectedCategories.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedCategories.map((cid) => {
                    const found = allCategories.find((x) => Number(x.id) === cid);
                    return (
                      <span
                        key={cid}
                        className="text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full"
                      >
                        {found ? found.slug : `#${cid}`}
                      </span>
                    );
                  })}
                </div>
              )}
            </section>

           {/* TAGS MULTI-SELECT (ACCORDION) */}
<section className="border rounded-xl overflow-hidden">
  {/* Header */}
  <button
    type="button"
    onClick={() => setIsTagsOpen((v) => !v)}
    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
  >
    <h2 className="text-lg font-semibold text-gray-800">{TEXT.tags}</h2>
    <span className="text-sm text-gray-500">
      {isTagsOpen ? "−" : "+"}
    </span>
  </button>

  {/* Content */}
  {isTagsOpen && (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {allTags.map((t) => {
          const tid = Number(t.id);
          const checked = selectedTags.includes(tid);

          return (
            <label
              key={t.id}
              className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer text-sm
                ${checked ? "bg-gray-100 border-gray-300" : "bg-white hover:bg-gray-50"}
              `}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => setSelectedTags((prev) => toggleIdInArray(prev, tid))}
              />
              <span className="truncate">#{t.slug}</span>
            </label>
          );
        })}
      </div>

      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tid) => {
            const found = allTags.find((x) => Number(x.id) === tid);
            return (
              <span
                key={tid}
                className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full"
              >
                #{found ? found.slug : `#${tid}`}
              </span>
            );
          })}
        </div>
      )}
    </div>
  )}
</section>

            {/* VARIANTS EDITOR */}
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-lg font-semibold text-gray-800">{TEXT.variants}</h2>

                <button
                  type="button"
                  onClick={addColorGroup}
                  className="px-3 py-2 rounded-xl bg-cyan-600 text-white text-sm flex items-center gap-2 hover:bg-cyan-700"
                >
                  <FaPlus /> {TEXT.addColor}
                </button>
              </div>

              <div className="space-y-4">
                {groupedVariants.length === 0 ? (
                  <div className="text-sm text-gray-500 bg-gray-50 border rounded-xl p-4">
                    No variants yet. Click <b>{TEXT.addColor}</b> to start.
                  </div>
                ) : (
                  groupedVariants.map((g, idx) => (
                    <div key={`${g.color_name}-${g.color_hex}-${idx}`} className="border rounded-2xl p-4">
                      {/* color header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className="w-5 h-5 rounded-full border"
                            style={{ backgroundColor: g.color_hex }}
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
  type="text"
  value={g.color_name || "Select color"}
  readOnly
  className="p-2 border rounded-xl text-sm bg-gray-100 cursor-not-allowed"
/>

                            <div className="flex items-center gap-2">
 <div className="flex flex-wrap gap-2">
  {WOMEN_COLOR_PALETTE.map((c) => {
    const active = g.color_hex === c.hex;

    return (
      <button
        key={c.hex}
        type="button"
        onClick={() =>
          updateColorGroup(idx, {
            color_hex: c.hex,
            color_name: c.name,
          })
        }
        title={c.name}
        className={`w-8 h-8 rounded-full border-2 transition
          ${active ? "border-black scale-110" : "border-gray-300 hover:scale-105"}
        `}
        style={{ backgroundColor: c.hex }}
      />
    );
  })}
</div>

{/* CUSTOM COLOR ADDER */}
<div className="mt-3 flex flex-wrap gap-2 items-center">
  <input
    type="text"
    placeholder="#ffffff"
    value={customHex}
    onChange={(e) => setCustomHex(e.target.value)}
    className="w-28 p-2 border rounded-lg text-sm"
  />

  <input
    type="text"
    placeholder="Optional name (e.g. Sky Blue)"
    value={customColorName}
    onChange={(e) => setCustomColorName(e.target.value)}
    className="w-36 p-2 border rounded-lg text-sm"
  />

  <button
    type="button"
    disabled={!isValidHex(customHex)}
    onClick={() => {
      const finalName =
        customColorName.trim() || customHex.toUpperCase();

      updateColorGroup(idx, {
        color_hex: customHex.toLowerCase(),
        color_name: finalName,
      });

      setCustomHex("");
      setCustomColorName("");
    }}
    className="px-3 py-2 rounded-lg bg-cyan-600 text-white text-sm disabled:opacity-40"
  >
    Add
  </button>

  {isValidHex(customHex) && (
    <span
      className="w-6 h-6 rounded-full border"
      title={customColorName || customHex.toUpperCase()}
      style={{ backgroundColor: customHex }}
    />
  )}
</div>




</div>

                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => addSizeRow(idx)}
                            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-2"
                          >
                            <FaPlus /> {TEXT.addSize}
                          </button>

                          <button
                            type="button"
                            onClick={() => removeColorGroup(idx)}
                            className="px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 text-sm flex items-center gap-2"
                          >
                            <FaTrash /> {TEXT.remove}
                          </button>
                        </div>
                      </div>

                      {/* sizes table (responsive) */}
                      <div className="mt-4 grid grid-cols-1 gap-2">
                        <div className="hidden sm:grid grid-cols-12 gap-2 text-xs text-gray-500 px-2">
                          <div className="col-span-5">{TEXT.size}</div>
                          <div className="col-span-5">Availability</div>
                          <div className="col-span-2"></div>
                        </div>

                        {g.rows.map((r, rowIdx) => (
                          <div
                            key={rowIdx}
                            className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-gray-50 border rounded-xl p-2"
                          >
                            <div className="sm:col-span-5">
                              <input
                                type="text"
                                value={r.size}
                                onChange={(e) => updateSizeRow(idx, rowIdx, { size: e.target.value })}
                                className="w-full p-2 border rounded-xl text-sm"
                                placeholder={TEXT.size}
                              />
                            </div>

                            <div className="sm:col-span-5">
                              <div className="flex items-center gap-4">
  <label className="flex items-center gap-2 text-green-700 font-semibold">
    <input
      type="radio"
      name={`available-${idx}-${rowIdx}`}
      checked={r.is_available === 1}
      onChange={() =>
        updateSizeRow(idx, rowIdx, { is_available: 1 })
      }
    />
    Available
  </label>

  <label className="flex items-center gap-2 text-red-600 font-semibold">
    <input
      type="radio"
      name={`available-${idx}-${rowIdx}`}
      checked={r.is_available === 0}
      onChange={() =>
        updateSizeRow(idx, rowIdx, { is_available: 0 })
      }
    />
    Not Available
  </label>
</div>

                            </div>

                            <div className="sm:col-span-2 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeSizeRow(idx, rowIdx)}
                                className="px-3 py-2 rounded-xl bg-white border hover:bg-gray-100 text-sm flex items-center gap-2"
                              >
                                <FaTrash /> {TEXT.remove}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

{/* IMAGES (same as Add Product) */}
<section>
  {/* MULTI UPLOAD */}
  <div className="flex gap-2">
    <input
      id="multi-image-upload"
      type="file"
      accept="image/*,video/*"
      multiple
      className="hidden"
      onChange={(e) => {
        const files = Array.from(e.target.files || []);
        handleMultiImageUpload(files);
        e.target.value = "";
      }}
    />

    <label
      htmlFor="multi-image-upload"
      className="
        px-3 py-2 rounded-xl
        bg-cyan-600 text-white text-sm
        flex items-center gap-2
        hover:bg-cyan-700
        cursor-pointer
      "
    >
      <FaImage /> Upload Images
    </label>
  </div>

  {/* GLOBAL ALT INPUTS */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        Alt (EN) — applies to all images
      </label>
      <input
        value={globalAltEn}
        onChange={(e) => setGlobalAltEn(e.target.value)}
        className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
        placeholder="e.g. Black leather boots"
      />
    </div>

    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        Alt (AR) — applies to all images
      </label>
      <input
        value={globalAltAr}
        onChange={(e) => setGlobalAltAr(e.target.value)}
        className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-cyan-400"
        placeholder="مثال: حذاء جلدي أسود"
      />
    </div>
  </div>

  {/* IMAGE GRID */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3">
    {images.map((img, idx) => (
      <div key={idx} className="border rounded-xl p-3 bg-gray-50">
        <div className="flex flex-col gap-2">

          {/* PREVIEW */}
          <div className="border rounded-lg bg-white p-2 flex items-center justify-center min-h-[80px]">
            {img.url ? (
              img.media_type === "video" ? (
                <video
                  src={img.url}
                  controls
                  className="w-28 h-20 object-contain rounded"
                />
              ) : (
                <img
                  src={img.url}
                  alt="Preview"
                  className="w-20 h-20 object-contain rounded"
                />
              )
            ) : (
              <FaImage className="text-gray-300 text-3xl" />
            )}
          </div>

          {/* SORT ORDER */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {TEXT.sortOrder}
            </label>
            <input
              type="number"
              value={img.sort_order}
              onChange={(e) =>
                updateImageRow(idx, { sort_order: Number(e.target.value || 0) })
              }
              className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-400"
            />
          </div>

          {/* COLOR */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Image Color
            </label>
            <input
              value={img.color_name || ""}
              onChange={(e) =>
                updateImageRow(idx, { color_name: e.target.value })
              }
              placeholder="Black, White…"
              className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-cyan-400"
            />
          </div>

          {/* REMOVE */}
          <button
            type="button"
            onClick={() => removeImageRow(idx)}
            className="mt-1 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs flex items-center justify-center gap-2"
          >
            <FaTrash /> {TEXT.remove}
          </button>

        </div>
      </div>
    ))}
  </div>
</section>


            {/* ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate("/admin/products")}
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 w-full sm:w-auto"
              >
                {TEXT.cancel}
              </button>

              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 rounded-xl bg-cyan-600 text-white flex items-center gap-2 hover:bg-cyan-700 disabled:opacity-60 w-full sm:w-auto"
              >
                <FaSave />
                {saving ? TEXT.saving : TEXT.update}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

export default AdminEditProduct;

